// Standalone product-redirect placeholder engine. Intentionally does NOT import from
// manual-gateway-template.ts: that file is consumed via its own package subpath export
// (@shoppex/contracts/manual-gateway-template), and a cross-subpath relative import here
// breaks Next.js/Turbopack module resolution for client components that only depend on
// this subpath. The scan/render logic is small enough to duplicate safely.

export const PRODUCT_REDIRECT_TEMPLATE_VARIABLES = ['product_id', 'order_id'] as const;

export type ProductRedirectTemplateVariable = typeof PRODUCT_REDIRECT_TEMPLATE_VARIABLES[number];
export type ProductRedirectTemplateVars = Record<ProductRedirectTemplateVariable, string>;

export const PRODUCT_REDIRECT_TEMPLATE_VARIABLE_EXAMPLES = [
  '{{product_id}}',
  '{{order_id}}',
] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildVariablePattern(key: string, style: 'double' | 'single'): RegExp {
  const escapedKey = escapeRegExp(key);
  if (style === 'double') {
    return new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'gi');
  }

  return new RegExp(`(?<!\\{)\\{\\s*${escapedKey}\\s*\\}(?!\\})`, 'gi');
}

// Accepts a partial var map: only the keys present (and non-empty) are
// substituted. A variable the caller cannot supply is left as a raw placeholder
// so downstream fail-closed checks (findRemainingProductRedirectPlaceholders)
// reject the URL instead of emitting an encoded "" / "null".
export function renderProductRedirectTemplate(
  template: string,
  vars: Partial<ProductRedirectTemplateVars>,
): string {
  let rendered = template;
  for (const [key, value] of Object.entries(vars)) {
    if (typeof value !== 'string' || value.length === 0) continue;
    const replacement = encodeURIComponent(value);
    rendered = rendered.replace(buildVariablePattern(key, 'double'), replacement);
    rendered = rendered.replace(buildVariablePattern(key, 'single'), replacement);
  }
  return rendered;
}

const PLACEHOLDER_SCAN_PATTERNS = [
  /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g,
  /(?<!\{)\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}(?!\})/g,
] as const;

const REMAINING_PLACEHOLDER_PATTERNS = [
  /\{\{[\s\S]*?\}\}/g,
  /(?<!\{)\{[^{}]+\}(?!\})/g,
] as const;

// A fully rendered redirect URL never contains a literal curly brace — any `{`
// or `}` left after substitution is a malformed/unrendered placeholder fragment
// (e.g. the unbalanced `{{product_id` or trailing `}` in `{{product_id}}}`).
// The balanced-span patterns above miss these, so scan for any brace that
// survives once the balanced spans are stripped out.
function findDanglingBraceFragments(template: string): string[] {
  let stripped = template;
  for (const pattern of REMAINING_PLACEHOLDER_PATTERNS) {
    stripped = stripped.replace(pattern, '');
  }
  return /[{}]/.test(stripped) ? [...new Set(stripped.match(/[{}]+/g) ?? [])] : [];
}

export function findUnsupportedProductRedirectPlaceholders(template: string): string[] {
  if (!template.trim()) {
    return [];
  }

  const allowedVariables = new Set<string>(PRODUCT_REDIRECT_TEMPLATE_VARIABLES);
  const unsupported = new Set<string>();
  const allowedRawSpans = new Set<string>();

  for (const pattern of PLACEHOLDER_SCAN_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      const raw = match[0];
      const name = match[1]?.toLowerCase();
      if (name && !allowedVariables.has(name)) {
        unsupported.add(raw);
      } else if (name) {
        allowedRawSpans.add(raw);
      }
    }
  }

  // Also reject malformed placeholder syntax (e.g. {{order-id}}, {{ product_id | x }}) that the
  // strict name patterns above don't match — same rationale as the manual-gateway scanner: catch
  // it at save time instead of letting a raw token survive to checkout.
  for (const pattern of REMAINING_PLACEHOLDER_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      const raw = match[0];
      if (!allowedRawSpans.has(raw)) {
        unsupported.add(raw);
      }
    }
  }

  // Reject unbalanced braces (e.g. `{{product_id` or `{{product_id}}}`) that the
  // balanced-span patterns never see — fail closed at save time rather than
  // letting a malformed token reach checkout.
  for (const fragment of findDanglingBraceFragments(template)) {
    unsupported.add(fragment);
  }

  return [...unsupported];
}

export function findRemainingProductRedirectPlaceholders(template: string): string[] {
  if (!template.trim()) {
    return [];
  }

  const remaining = new Set<string>();
  for (const pattern of REMAINING_PLACEHOLDER_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      remaining.add(match[0]);
    }
  }

  // A rendered URL must not carry any leftover brace — including unbalanced ones
  // the balanced-span patterns miss — or checkout would redirect to a literal
  // malformed token instead of failing closed.
  for (const fragment of findDanglingBraceFragments(template)) {
    remaining.add(fragment);
  }

  return [...remaining];
}

export function isSafeHttpsRedirectTemplateUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
